import {
  CareerSiteOpeningV2,
  CareerSiteOpeningV2Response,
  CareerSiteSettings,
  PaginateJsonSchema,
} from 'api-clients/monolith';
import { getSortOptions } from 'constants/v2/sort-options';
import { AppContext } from 'contexts/v2/app-context';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Skeleton } from 'components/ui/skeleton';

import noJobsFoundImage from '../../../images/no-jobs-found.svg';
import { JobsList } from '../jobs-list';
import { Pagination, PaginationSkeleton } from '../pagination';
import { RoundedActiveFilters } from '../rounded-active-filters';
import { SearchSelect } from '../search-select';

export function JobsSectionSkeleton() {
  return (
    <div className="flex flex-col gap-4" data-testid="jobs-section-skeleton">
      <div className="flex items-center justify-between lg:hidden">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-8 w-32" />
      </div>

      <div className="hidden items-center justify-between lg:flex">
        <Skeleton className="h-5 w-full max-w-md" />
        <Skeleton className="h-8 w-32" />
      </div>

      <div className="lg:hidden">
        <Skeleton className="h-5 w-full max-w-md" />
      </div>

      <JobsList.SkeletonGroup />
      <PaginationSkeleton />
    </div>
  );
}

interface JobsSectionProps {
  jobs: CareerSiteOpeningV2Response['openings'];
  paginationData?: CareerSiteOpeningV2Response['pagination'];
  isCurrentLocation: boolean;
  currentCity: string | null;
  mobileFiltersDrawer?: React.ReactNode;
  brandSetting?: CareerSiteSettings;
}

export function JobsSection({
  jobs,
  paginationData,
  isCurrentLocation,
  currentCity,
  mobileFiltersDrawer,
  brandSetting,
}: JobsSectionProps) {
  const {
    isFetchingJobs,
    page,
    setPage,
    sortBy,
    setSortBy,
    updateUrlWithParams,
    accountSlug,
    brandId,
    activeFiltersV2,
    currentState,
    currentCountry,
    setIsFetchingJobs,
  } = useContext(AppContext);

  const intl = useIntl();
  const sortOptions = getSortOptions(intl);

  const hasLocationFilters =
    activeFiltersV2.city?.length ||
    activeFiltersV2.state?.length ||
    activeFiltersV2.country?.length;

  const isLoading =
    isFetchingJobs ||
    (!hasLocationFilters && isCurrentLocation && currentCity === null);

  const fullLocationString = [currentCity, currentState, currentCountry]
    .filter(Boolean)
    .join(', ');

  const sortByDropdown = (
    <div className="inline-flex shrink-0 items-center text-sm font-medium">
      <SearchSelect
        value={sortBy.key}
        options={sortOptions}
        onChange={(value: string) => {
          const selectedOption =
            sortOptions.find(opt => opt.key === value) ?? sortOptions[0];
          setSortBy(selectedOption);

          if (accountSlug) {
            setIsFetchingJobs(true);
            updateUrlWithParams(accountSlug, brandId, 'sort_by', value);
            updateUrlWithParams(accountSlug, brandId, 'page', '1');
          }
        }}
        variant="text"
        label={intl.formatMessage({
          defaultMessage: 'Sort by:',
          description: 'Label for sort options dropdown',
        })}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between lg:hidden">
        <div>{mobileFiltersDrawer}</div>
        {sortByDropdown}
      </div>

      <div className="hidden items-center justify-between text-sm text-gray-600 lg:flex">
        {isLoading ? (
          <Skeleton className="h-5 w-64" />
        ) : (
          <JobsSection.Title
            jobs={jobs}
            paginationData={paginationData}
            fullLocationString={fullLocationString}
          />
        )}
        {sortByDropdown}
      </div>

      <div className="text-sm text-gray-600 lg:hidden">
        {isLoading ? (
          <Skeleton className="h-5 w-48" />
        ) : (
          <JobsSection.Title
            jobs={jobs}
            paginationData={paginationData}
            fullLocationString={fullLocationString}
          />
        )}
      </div>

      <RoundedActiveFilters />

      {isLoading ? (
        <>
          <JobsList.SkeletonGroup />
          <PaginationSkeleton />
        </>
      ) : jobs.length > 0 ? (
        <>
          <JobsList jobs={jobs} brandSetting={brandSetting} />
          <Pagination
            currentPage={page}
            lastPage={paginationData?.total_pages ?? 1}
            setPage={setPage}
            jobsCount={paginationData?.total_count ?? 0}
            perPage={paginationData?.per_page ?? 20}
          />
        </>
      ) : (
        <div
          className="flex flex-col items-center justify-center py-8"
          data-testid="no-jobs-found"
        >
          <img src={noJobsFoundImage} alt="No jobs found" className="w-40" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            <FormattedMessage
              defaultMessage="No jobs found"
              description="Title shown when no jobs match the search criteria"
            />
          </h3>
          <p className="text-center text-sm text-gray-600">
            <FormattedMessage
              defaultMessage="Please adjust your search and filters"
              description="Message shown when no jobs match the search criteria"
            />
          </p>
        </div>
      )}
    </div>
  );
}

interface JobsSectionTitleProps {
  jobs: CareerSiteOpeningV2[];
  paginationData?: PaginateJsonSchema;
  isCurrentLocation: boolean;
  fullLocationString: string;
}

JobsSection.Title = function JobsSectionTitle({
  jobs,
  paginationData,
  fullLocationString,
}: Omit<JobsSectionTitleProps, 'isCurrentLocation'>) {
  const { jobSearchQuery, radius, location, activeFiltersV2 } =
    useContext(AppContext);
  const intl = useIntl();

  const count = jobs?.length ? paginationData?.total_count : 0;
  const isGlobalSearch = location.value === 'all';
  const isRemoteLocation = location.value?.toLowerCase() === 'remote';
  const isCurrentLocation = location.value === 'current_location';
  const hasLocationFilters = Boolean(
    activeFiltersV2.city?.length ||
    activeFiltersV2.state?.length ||
    activeFiltersV2.country?.length,
  );
  const isAnyRadius = radius?.key.includes('any');

  const unit = radius?.key.includes('km')
    ? intl.formatMessage({
        defaultMessage: 'kilometers',
        description: 'Distance unit for kilometers',
      })
    : intl.formatMessage({
        defaultMessage: 'miles',
        description: 'Distance unit for miles',
      });

  const locationText = isCurrentLocation
    ? fullLocationString
    : (location.value ?? fullLocationString);

  const radiusValue = radius?.key.replace(/[^\d]/g, '') ?? '50';
  const query = jobSearchQuery ? `"${jobSearchQuery}"` : '';
  const locationFormatted = `"${String(locationText)}"`;

  if (isGlobalSearch) {
    if (hasLocationFilters && jobSearchQuery) {
      return (
        <FormattedMessage
          defaultMessage="{count, plural, one {# job} other {# jobs}} available for {query}"
          description="Jobs count: global search with filters and query"
          values={{ count, query }}
        />
      );
    }
    if (hasLocationFilters) {
      return (
        <FormattedMessage
          defaultMessage="{count, plural, one {# job} other {# jobs}} available"
          description="Jobs count: global search with filters"
          values={{ count }}
        />
      );
    }
    if (jobSearchQuery) {
      return (
        <FormattedMessage
          defaultMessage="{count, plural, one {# job} other {# jobs}} available for {query} (all locations)"
          description="Jobs count: global search with query"
          values={{ count, query }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="{count, plural, one {# job} other {# jobs}} available (all locations)"
        description="Jobs count: global search"
        values={{ count }}
      />
    );
  }

  if (isRemoteLocation) {
    if (jobSearchQuery) {
      return (
        <FormattedMessage
          defaultMessage="{count, plural, one {# remote job} other {# remote jobs}} found for {query}"
          description="Jobs count: remote with query"
          values={{ count, query }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="{count, plural, one {# remote job} other {# remote jobs}} found"
        description="Jobs count: remote"
        values={{ count }}
      />
    );
  }

  if (isAnyRadius) {
    if (jobSearchQuery) {
      return (
        <FormattedMessage
          defaultMessage="{count, plural, one {# job} other {# jobs}} found for {query} near {location}"
          description="Jobs count: location with any radius and query"
          values={{ count, query, location: locationFormatted }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="{count, plural, one {# job} other {# jobs}} found near {location}"
        description="Jobs count: location with any radius"
        values={{ count, location: locationFormatted }}
      />
    );
  }

  if (jobSearchQuery) {
    return (
      <FormattedMessage
        defaultMessage="{count, plural, one {# job} other {# jobs}} found for {query} within {radius} {unit} of {location}"
        description="Jobs count: location with radius and query"
        values={{
          count,
          query,
          radius: radiusValue,
          unit,
          location: locationFormatted,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      defaultMessage="{count, plural, one {# job} other {# jobs}} found within {radius} {unit} of {location}"
      description="Jobs count: location with radius"
      values={{ count, radius: radiusValue, unit, location: locationFormatted }}
    />
  );
};
